import { useState, useCallback } from "react";
import { UserEntity } from "../../domain/entities/UserEntity";
import { CompanyEntity } from "../../domain/entities/CompanyEntity";
import { UserRepositoryImpl } from "../../data/repositories/UserRepositoryImpl";
import {
  CreateUserUseCase,
  GetUsersUseCase,
  DeleteUserUseCase,
} from "../../domain/use-cases/users";

// Create repository instance (singleton)
const userRepository = new UserRepositoryImpl();

// Create use cases
const createUserUseCase = new CreateUserUseCase(userRepository);
const getUsersUseCase = new GetUsersUseCase(userRepository);
const deleteUserUseCase = new DeleteUserUseCase(userRepository);

export interface UseUsersReturn {
  users: UserEntity[];
  pagination: {
    current_page: number;
    per_page: number;
    total: number;
    total_pages: number;
  };
  loading: boolean;
  error: Error | null;
  fetchUsers: (page?: number, limit?: number, search?: string) => Promise<void>;
  getUser: (userId: number) => Promise<{
    user: UserEntity;
    assigned_companies: Array<{
      id: number;
      company_name: string;
      app_url: string;
      role_name: string | null;
      is_company_admin: boolean;
    }>;
    available_companies: CompanyEntity[];
  }>;
  createUser: (data: {
    email: string;
    password: string;
    userName?: string;
    company_ids?: number[];
    roles?: { [companyId: number]: string };
  }) => Promise<{
    user: UserEntity;
    assigned_companies: CompanyEntity[];
  }>;
  updateUser: (
    userId: number,
    data: Partial<UserEntity> & {
      assigned_company_ids?: number[];
      unassigned_company_ids?: number[];
      roles?: { [companyId: number]: string };
    }
  ) => Promise<UserEntity>;
  deleteUser: (userId: number) => Promise<void>;
}

export const useUsers = (): UseUsersReturn => {
  const [users, setUsers] = useState<UserEntity[]>([]);
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 50,
    total: 0,
    total_pages: 1,
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchUsers = useCallback(
    async (page: number = 1, limit: number = 50, search?: string) => {
      try {
        setLoading(true);
        setError(null);
        const result = await getUsersUseCase.execute(page, limit, search);
        setUsers(result.users);
        setPagination(result.pagination);
      } catch (err) {
        setError(
          err instanceof Error ? err : new Error("Failed to fetch users")
        );
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const getUser = useCallback(async (userId: number) => {
    try {
      setError(null);
      return await userRepository.getUserById(userId);
    } catch (err) {
      setError(
        err instanceof Error ? err : new Error("Failed to fetch user")
      );
      throw err;
    }
  }, []);

  const createUser = useCallback(
    async (data: {
      email: string;
      password: string;
      userName?: string;
      company_ids?: number[];
      roles?: { [companyId: number]: string };
    }) => {
      try {
        setLoading(true);
        setError(null);
        const result = await createUserUseCase.execute(data);
        // Refresh users list
        await fetchUsers(pagination.current_page, pagination.per_page);
        return result;
      } catch (err) {
        setError(
          err instanceof Error ? err : new Error("Failed to create user")
        );
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [fetchUsers, pagination]
  );

  const updateUser = useCallback(
    async (
      userId: number,
      data: Partial<UserEntity> & {
        assigned_company_ids?: number[];
        unassigned_company_ids?: number[];
        roles?: { [companyId: number]: string };
      }
    ) => {
      try {
        setError(null);
        const result = await userRepository.updateUser(userId, data);
        // Refresh users list
        await fetchUsers(pagination.current_page, pagination.per_page);
        return result;
      } catch (err) {
        setError(
          err instanceof Error ? err : new Error("Failed to update user")
        );
        throw err;
      }
    },
    [fetchUsers, pagination]
  );

  const deleteUser = useCallback(
    async (userId: number) => {
      try {
        setError(null);
        await deleteUserUseCase.execute(userId);
        // Refresh users list
        await fetchUsers(pagination.current_page, pagination.per_page);
      } catch (err) {
        setError(
          err instanceof Error ? err : new Error("Failed to delete user")
        );
        throw err;
      }
    },
    [fetchUsers, pagination]
  );

  return {
    users,
    pagination,
    loading,
    error,
    fetchUsers,
    getUser,
    createUser,
    updateUser,
    deleteUser,
  };
};

